import React, { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { celebrate } from "../lib/achievements";
import { SocialShareModal } from "../components/SocialShare";
import { buildAchievementCard } from "../lib/shareCard";
import {
  Sprout, Scroll, Book, Library, Flame, Trophy, RefreshCcw, Crown, Star,
  Heart, Lightbulb, Lock, Award, Share2, UserPlus, Copy, Check,
} from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "../i18n/LanguageContext";

const ICONS = {
  sprout: Sprout, scroll: Scroll, book: Book, library: Library, flame: Flame,
  trophy: Trophy, refresh: RefreshCcw, crown: Crown, star: Star, heart: Heart, lightbulb: Lightbulb,
};

export default function Achievements() {
  const { tx, t } = useLanguage();
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [stats, setStats] = useState(null);
  const [sharing, setSharing] = useState(null); // achievement being shared
  const [referrals, setReferrals] = useState(null);
  const [copied, setCopied] = useState(false);

  const refLink = referrals?.ref_code
    ? `${window.location.origin}/?ref=${referrals.ref_code}`
    : window.location.origin;

  const buildCanvas = useCallback(() => buildAchievementCard({
    name: user?.name || "A HikmahVerses student",
    title: sharing?.title || "",
    desc: sharing?.desc || "",
    url: window.location.origin.replace(/^https?:\/\//, ""),
  }), [sharing, user]);

  useEffect(() => {
    api.get("/achievements").then((r) => {
      setItems(r.data);
      if (r.data.some((a) => a.new)) celebrate();
    }).catch(() => setItems([]));
    api.get("/hifz/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/referrals").then((r) => setReferrals(r.data)).catch(() => {});
  }, []);

  const earned = (items || []).filter((a) => a.earned);

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="achievements-page">
      <header>
        <div className="text-xs uppercase tracking-[0.22em] text-gold-600 font-semibold">{t("achievements.motivation")}</div>
        <h1 className="mt-1 font-display font-semibold text-4xl text-forest-700">{t("achievements.achievements")}</h1>
        <p className="mt-2 text-foreground/70">{t("achievements.everyBadgeMarksAMilestone")}</p>
      </header>

      {stats && (
        <section className="hifz-card grain bg-forest-700 border-forest-700 text-sand-100" data-testid="level-card">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4">
              <div className="h-14 w-14 rounded-full bg-gold-500 text-foreground grid place-items-center font-display font-bold text-xl">
                {stats.level}
              </div>
              <div>
                <div className="font-display font-semibold text-xl">{t("achievements.level", { level: stats.level })}</div>
                <div className="text-sm text-sand-100/70">{t("achievements.xpProgress", { xp: stats.xp, remaining: stats.next_level_xp - stats.xp, level: stats.level + 1 })}</div>
              </div>
            </div>
            <div className="text-sm text-sand-100/80">{t("achievements.badgesEarned", { earned: earned.length, total: items?.length ?? 11 })}</div>
          </div>
          <div className="mt-4 h-2.5 rounded-full bg-sand-100/15 overflow-hidden">
            <div className="h-full bg-gold-500 transition-all duration-700" style={{ width: `${stats.level_progress}%` }} />
          </div>
        </section>
      )}

      {!items ? (
        <div className="grid gap-4">
          {[...Array(6)].map((_, i) => <div key={i} className="hifz-card h-40 animate-pulse-soft bg-sand-200/50" />)}
        </div>
      ) : (
        <div className="grid gap-4" data-testid="achievements-grid">
          {items.map((a) => {
            const Icon = ICONS[a.icon] || Award;
            return (
              <div key={a.id}
                className={`hifz-card relative overflow-hidden transition ${a.earned ? "" : "opacity-70"}`}
                data-testid={`achievement-${a.id}`}>
                <div className={`h-12 w-12 rounded-xl grid place-items-center ${
                  a.earned ? "bg-gold-500/15 text-gold-600" : "bg-sand-200 text-sand-400"
                }`}>
                  {a.earned ? <Icon className="h-6 w-6" /> : <Lock className="h-5 w-5" />}
                </div>
                <div className="mt-4 font-display font-semibold text-forest-700">{tx(a.title)}</div>
                <p className="mt-1 text-xs text-muted-foreground leading-relaxed">{tx(a.desc)}</p>
                {a.earned ? (
                  <div className="mt-3 flex items-center justify-between gap-2">
                    <div className="text-[11px] font-semibold text-gold-700">
                      {t("achievements.earnedOn", { date: a.earned_at ? new Date(a.earned_at).toLocaleDateString() : "" })}
                    </div>
                    <button onClick={() => setSharing(a)}
                      className="inline-flex items-center gap-1 text-[11px] font-semibold text-forest-700 hover:text-gold-700 transition"
                      data-testid={`share-achievement-${a.id}`}>
                      <Share2 className="h-3 w-3" /> {t("achievements.share")}
                    </button>
                  </div>
                ) : (
                  <div className="mt-3">
                    <div className="h-1.5 rounded-full bg-sand-200 overflow-hidden">
                      <div className="h-full bg-forest-600/60" style={{ width: `${a.progress}%` }} />
                    </div>
                    {a.progress_label && <div className="mt-1 text-[10px] text-muted-foreground">{a.progress_label}</div>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <SocialShareModal
        open={!!sharing}
        onClose={() => setSharing(null)}
        buildCanvas={buildCanvas}
        message={t("achievements.alhamdulillahIJustEarnedThe", { title: sharing?.title })}
        url={refLink}
        filename={`hikmahverses-${sharing?.id || "badge"}.png`}
      />

      {/* Invite friends / referrals */}
      <section className="hifz-card border-gold-500/40" data-testid="referral-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="h-11 w-11 rounded-xl bg-gold-500/15 grid place-items-center text-gold-600 shrink-0">
              <UserPlus className="h-5 w-5" />
            </div>
            <div>
              <h2 className="font-display font-semibold text-xl text-forest-700">{t("achievements.inviteFriends")}</h2>
              <p className="mt-1 text-sm text-muted-foreground">{t("achievements.shareYourPersonalLinkEvery")}</p>
              {referrals && (
                <div className="mt-2 text-sm font-semibold text-gold-700" data-testid="referral-count">
                  {referrals.count === 0
                    ? t("achievements.beTheFirstInYour")
                    : t("achievements.friendsJoined", { count: referrals.count })}
                </div>
              )}
              {referrals?.friends?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5" data-testid="referral-friends">
                  {referrals.friends.slice(0, 6).map((f, i) => (
                    <span key={i} className="px-2.5 py-0.5 rounded-full bg-forest-600/10 text-forest-700 text-xs">{f.name}</span>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 bg-white rounded-full border border-sand-300 px-3 py-1.5 text-xs" data-testid="referral-link-bar">
            <span className="text-muted-foreground truncate max-w-[220px]">{refLink}</span>
            <button
              onClick={async () => { await navigator.clipboard.writeText(refLink); setCopied(true); toast.success(t("achievements.inviteLinkCopied")); setTimeout(() => setCopied(false), 1500); }}
              className="text-forest-700 font-semibold inline-flex items-center gap-1"
              data-testid="referral-copy-btn">
              {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
              {copied ? t("achievements.copied") : t("achievements.copy")}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
